<template>
  <div 
    class="video-container" 
    :class="{'video-playing': isNormalPlayback}" 
    @click="handleContainerClick"
  >
    <video 
      ref="videoElement"
      muted 
      loop
    >
      <source :src="videoSrc" type="video/mp4">
      您的浏览器不支持视频标签。
    </video>
    <div class="play-button"></div>
    <div class="video-controls">
      <button class="control-button" @click.stop="togglePlayPause">
        {{ video && video.paused ? '▶' : '❚❚' }}
      </button>
      <div class="progress-bar" @click.stop="seek">
        <div class="progress" :style="{width: progress + '%'}"></div>
      </div>
      <button class="control-button" @click.stop="toggleMute">
        {{ video && video.muted ? '🔇' : '🔊' }}
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BackgroundVideoPlayer',
  props: {
    videoSrc: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      video: null,
      isNormalPlayback: false,
      progress: 0,
      progressInterval: null
    }
  },
  mounted() {
    this.video = this.$refs.videoElement;
    this.initBackgroundVideo();
    
    // 监听进度更新
    this.video.addEventListener('timeupdate', this.updateProgress);
    
    // 监听视频结束
    this.video.addEventListener('ended', this.handleVideoEnd);
  },
  beforeDestroy() {
    // 清除事件监听
    if (this.video) {
      this.video.removeEventListener('timeupdate', this.updateProgress);
      this.video.removeEventListener('ended', this.handleVideoEnd);
    }
  },
  methods: {
    // 初始化背景视频模式
    initBackgroundVideo() {
      if (!this.video) return;
      
      this.video.muted = true;
      this.video.loop = true;
      this.video.playbackRate = 0.75; // 背景模式下稍微慢一点
      
      // 尝试自动播放
      this.video.play().catch(e => {
        console.log('自动播放被阻止。请先与页面交互。');
      });
    },
    
    // 切换到正常播放模式
    enableNormalPlayback() {
      if (!this.video) return;
      
      this.isNormalPlayback = true;
      this.video.muted = false;
      this.video.loop = false;
      this.video.playbackRate = 1.0;
      
      // 从头开始播放
      this.video.currentTime = 0;
      this.video.play();
    },
    
    // 处理视频容器点击
    handleContainerClick(e) {
      // 忽略控制按钮的点击
      if (e.target.classList.contains('control-button') || 
          e.target.classList.contains('progress-bar') || 
          e.target.classList.contains('progress')) {
        return;
      }
      
      if (!this.isNormalPlayback) {
        this.enableNormalPlayback();
      } else {
        this.togglePlayPause();
      }
    },
    
    // 切换播放/暂停
    togglePlayPause() {
      if (!this.video) return;
      
      if (!this.isNormalPlayback) {
        this.enableNormalPlayback();
        return;
      }
      
      if (this.video.paused) {
        this.video.play();
      } else {
        this.video.pause();
      }
    },
    
    // 切换静音
    toggleMute() {
      if (!this.video) return;
      this.video.muted = !this.video.muted;
    },
    
    // 更新进度条
    updateProgress() {
      if (!this.video || this.video.duration === 0) return;
      this.progress = (this.video.currentTime / this.video.duration) * 100;
    },
    
    // 跳转到指定位置
    seek(e) {
      if (!this.video) return;
      
      if (!this.isNormalPlayback) {
        this.enableNormalPlayback();
      }
      
      const progressBar = e.currentTarget;
      const pos = (e.clientX - progressBar.getBoundingClientRect().left) / progressBar.offsetWidth;
      this.video.currentTime = pos * this.video.duration;
    },
    
    // 处理视频结束
    handleVideoEnd() {
      if (this.isNormalPlayback) {
        this.isNormalPlayback = false;
        this.initBackgroundVideo();
      }
    }
  }
}
</script>

<style scoped>
.video-container {
  position: relative;
  width: 640px;
  max-width: 100%;
  box-sizing: border-box;
  height: auto;
  background-color: #c69c56; /* 金褐色背景，与图片匹配 */
  cursor: pointer;
  overflow: hidden;
  border: 10px solid #f9f9f1;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  &.w-full {
    width: 100%;
    border: none;
  }
  max-height: calc(100vh - 100px);
}

video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.play-button {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80px;
  height: 80px;
  background-color: rgba(255, 255, 255, 0.7);
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: opacity 0.3s;
}

.play-button::after {
  content: '';
  display: block;
  width: 0;
  height: 0;
  border-top: 20px solid transparent;
  border-bottom: 20px solid transparent;
  border-left: 30px solid #c69c56;
  margin-left: 8px;
}

.video-playing .play-button {
  opacity: 0;
  pointer-events: none;
}

.video-controls {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  padding: 10px;
  box-sizing: border-box;
  opacity: 0;
  transition: opacity 0.3s;
}

.video-container:hover .video-controls,
.video-playing .video-controls {
  opacity: 1;
}

.progress-bar {
  flex-grow: 1;
  height: 5px;
  background-color: #444;
  margin: 0 10px;
  position: relative;
  cursor: pointer;
}

.progress {
  height: 100%;
  background-color: #c69c56;
  width: 0;
}

.control-button {
  background: none;
  border: none;
  color: white;
  font-size: 16px;
  cursor: pointer;
  padding: 0 10px;
}
</style>